<script setup>
import dayjs from 'dayjs'
import { computed } from 'vue'
import { useUiStore } from '@/stores/UiStore'
import relativeTime from 'dayjs/plugin/relativeTime'
import { UPDATE_TRUCK_LOCATION } from '@/constants/dispatch'
import useDateTimeFormatter from '@/composables/useDateTimeFormatter'

/**
 * The dayjs plugin.
 */
dayjs.extend(relativeTime)

/**
 * The component's composables.
 */
const { formatDateTime } = useDateTimeFormatter()

/**
 * The store instances.
 */
const uiStore = useUiStore()

/**
 * The component's props.
 */
const props = defineProps({
    /**
     * The current location.
     *
     * @type {Object}
     */
    currentLocation: {
        required: true,
    },

    /**
     * The current stop id.
     *
     * @type {Number}
     */
    currentStopId: {
        type: Number,
        required: true,
    },

    loadId: {
        type: Number,
        required: true,
    },

    /**
     * The non arrived stop.
     *
     * @type {Object}
     */
    stop: {
        type: Object,
        required: true,
    },
})

/**
 * The stop's actual arrival time.
 *
 * @type {String}
 */
const actualTime = computed(() => {
    if (!props.stop.actual.arrive_at) {
        return ''
    }

    return formatDateTime(props.stop.actual.arrive_at, 'hh:mma').replace(
        /^0+/,
        ''
    )
})

/**
 * The stop's actual arrival date.
 *
 * @type {String}
 */
const actualDate = computed(() => {
    return formatDateTime(props.stop.actual.arrive_at, 'MMM DD')
})

/**
 * The stop's address.
 *
 * @type {String}
 */
const address = computed(() => {
    const location = props.stop.location

    if (location) {
        return `${location.street1} ${location.street2 ?? ''}, ${
            cityState.value
        } ${location.zip ?? ''}`
    }

    return 'N/A'
})

/**
 * The stop's city and state.
 *
 * @type {String}
 */
const cityState = computed(() => {
    const location = props.stop.location

    if (!location) {
        return ''
    }

    return `${location?.city}, ${location?.state}`
})

/**
 * The current location received time.
 *
 * @type {String}
 */
const currentLocationReceivedTime = computed(() => {
    if (!props.currentLocation || !props.currentLocation?.received_at) {
        return
    }

    return dayjs().from(dayjs(props.currentLocation.received_at), 'day')
})

/**
 * Determine if the stop has arrived.
 *
 * @type {Boolean}
 */
const hasArrived = computed(() => {
    return !!props.stop.actual?.arrive_at
})

/**
 * Determine if the stop is the current stop.
 *
 * @type {Boolean}
 */
const isCurrentStop = computed(() => {
    return props.currentStopId === props.stop.id
})

/**
 * Determine if the stop is completed.
 *
 * @return {Boolean}
 */
const isCompletedStop = computed(() => {
    return !!props.stop.actual.arrive_at && !!props.stop.scheduled.arrive_at
})

/**
 * Determine if the stop is late.
 *
 * @return {Boolean}
 */
const isLateStop = computed(() => {
    return dayjs(props.stop.actual?.arrive_at).isAfter(
        dayjs(props.stop.scheduled?.arrive_at)
    )
})

/**
 * The stop's late time.
 *
 * @return {String}
 */
const lateTime = computed(() => {
    return dayjs(props.stop.actual?.arrive_at).from(
        dayjs(props.stop.scheduled?.arrive_at),
        'day'
    )
})

/**
 * The location's contact.
 *
 * @type {String}
 */
const locationContact = computed(() => {
    const location = props.stop?.location

    return `${location?.name ?? ''} ${location?.phone ?? ''} ${
        location?.email ?? ''
    }`
})

/**
 * The stop's scheduled arrival time.
 *
 * @type {String}
 */
const scheduledTime = computed(() => {
    if (!props.stop.scheduled.arrive_at) {
        return ''
    }

    return formatDateTime(props.stop.scheduled.arrive_at, 'hh:mma').replace(
        /^0+/,
        ''
    )
})

/**
 * The stop's scheduled arrival date.
 *
 * @type {String}
 */
const scheduledDate = computed(() => {
    return formatDateTime(props.stop.scheduled.arrive_at, 'MMM DD')
})

/**
 * The stop's type name.
 *
 * @type {String}
 */
const stopType = computed(() => {
    return props.stop?.stop_type?.name ?? 'N/A'
})

/**
 * The truck's location.
 *
 * @return {String}
 */
const truckLocation = computed(() => {
    if (!props.currentLocation) {
        return ''
    }

    const description = props.currentLocation.description

    if (description) {
        return description
    }

    const { geo = null } = props.currentLocation.geo

    if (!geo) {
        return ''
    }

    return `${geo.latitude}, ${geo.longitude}`
})

/**
 * Handle the update location click event.
 *
 * @return {void}
 */
const onUpdateLocationClick = () => {
    uiStore.openAction(UPDATE_TRUCK_LOCATION, props.loadId)
}

/**
 * Handle the set arrival link being clicked.
 *
 * @return {void}
 */
const onSetArrivalClick = () => {
    window.location.href = `/index.cfm?module=dispatch&template=dspLoad&intLoadId=${props.loadId}`
}
</script>

<template>
    <div v-if="isCurrentStop" class="current-location">
        <div class="flex items-center">
            <v-icon
                class="mr-2"
                size="x-small"
                icon="far fa-circle-dashed"
            ></v-icon>

            <div class="flex flex-col">
                <span class="text-xs text-gray-500 mr-3">
                    Current Location
                </span>

                <span class="text-xs" v-if="currentLocation?.geo">
                    {{ truckLocation }}
                </span>
                <span
                    v-if="!!currentLocation?.data_supplier?.name"
                    class="text-xs text-gray-500"
                >
                    via
                    {{ currentLocation?.data_supplier?.name }}
                    {{ currentLocationReceivedTime }}
                    ago
                </span>
            </div>
        </div>
        <div>
            <button
                @click.prevent="onUpdateLocationClick"
                class="text-xs text-blue-600"
            >
                Update Location
            </button>
        </div>
    </div>
    <v-expansion-panel class="!rounded-none">
        <v-expansion-panel-title
            hide-actions
            class="flex justify-between w-full"
            :class="[isCompletedStop ? 'bg-gray-100' : 'bg-white']"
        >
            <div class="flex items-center">
                <v-icon
                    v-if="hasArrived"
                    class="mr-2"
                    size="x-small"
                    :color="isLateStop ? 'red' : 'green'"
                    icon="fas fa-circle-check"
                ></v-icon>
                <v-icon
                    v-else
                    class="mr-2"
                    size="x-small"
                    icon="far fa-circle"
                ></v-icon>
                <div class="flex flex-col">
                    <span class="text-xs text-gray-500 mr-3">
                        {{ stopType }}
                        ({{ (stop?.distance_from_last_stop || 0).toFixed() }}
                        mi)
                    </span>
                    <span class="text-xs">{{ cityState }}</span>
                </div>
            </div>
            <div class="flex flex-col items-center justify-between w-36">
                <span
                    v-if="isLateStop"
                    class="text-red-600 text-xs text-right w-full"
                >
                    Arrived {{ lateTime }} late
                </span>
                <div class="flex items-center justify-end w-full">
                    <v-icon
                        size="xx-small"
                        icon="far fa-circle-s"
                        class="mr-1"
                    ></v-icon>
                    <span class="text-sm text-nor">
                        {{ scheduledTime }} &#183; {{ scheduledDate }}
                    </span>
                </div>
                <div
                    v-if="actualTime && actualDate"
                    class="flex justify-end items-center w-full"
                >
                    <v-icon
                        size="xx-small"
                        icon="far fa-circle-a"
                        class="mr-1"
                    ></v-icon>
                    <span class="text-sm">
                        {{ actualTime }} &#183; {{ actualDate }}
                    </span>
                </div>
                <div v-else class="w-full flex justify-end">
                    <a
                        @click.prevent.stop="onSetArrivalClick"
                        href="#"
                        class="text-sm text-blue-600 text-end z-10"
                    >
                        Set Arrival
                    </a>
                </div>
            </div>
        </v-expansion-panel-title>
        <v-expansion-panel-text
            :class="[isCompletedStop ? 'bg-gray-100' : '!bg-white']"
        >
            <div
                class="flex flex-col px-8 text-sm"
                :class="[isCompletedStop ? 'bg-gray-100' : 'bg-white']"
            >
                <v-divider
                    class="border-opacity-100 border-black mb-3"
                ></v-divider>
                <div class="flex mb-2">
                    <span class="w-1/2"> Stop address </span>
                    <span class="w-1/2">
                        {{ address }}
                    </span>
                </div>

                <div class="flex mb-2">
                    <span class="w-1/2"> Bill of lading </span>
                    <span class="w-1/2"> {{ stop.bill_of_lading ?? '-' }}</span>
                </div>

                <div class="flex mb-2">
                    <span class="w-1/2"> Location contact </span>
                    <span class="w-1/2">
                        {{ locationContact }}
                    </span>
                </div>

                <div class="flex mb-2">
                    <span class="w-1/2"> Local directions </span>
                    <span class="w-1/2">
                        {{ stop.location?.local_directions ?? '-' }}
                    </span>
                </div>

                <div class="flex mb-2">
                    <span class="w-1/2"> Location comments </span>
                    <span class="w-1/2">
                        {{ stop?.location?.comments ?? '-' }}
                    </span>
                </div>
            </div>
        </v-expansion-panel-text>
    </v-expansion-panel>
</template>
<style scoped>
.current-location {
    @apply bg-slate-200;

    align-items: center;
    display: flex;
    height: 4.787rem;
    justify-content: space-between;
    padding: 16px 24px;
    width: 100%;
}
</style>
